import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { isEmptyStringOrNull } from '@sironamedical/common';
import { ExamCommentsModalSection } from 'common/ExamComment/ExamCommentsModalSection';
import { useExamComments } from 'common/ExamComment/useExamComments';
import { Button } from 'common/ui/Button';
import { Stack } from 'common/ui/Layout';
import Text from 'common/ui/Text';
import { telemetryContextFor } from 'modules/Apollo/types';
import React, { useState } from 'react';
import { Spacings } from 'styles/index';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { logger } from 'modules/logger';

type CriticalResultsModalProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  title: string;
  titleIcon?: React.ReactNode;
  body: React.ReactElement;
  worklistItemSmid: string;
  actionLabel: string;
  onSubmit: () => Promise<void>;
};

export const CriticalResultsModal = ({
  isOpen,
  setIsOpen,
  title,
  body,
  worklistItemSmid,
  onSubmit,
  actionLabel,
  titleIcon,
}: CriticalResultsModalProps) => {
  const [comment, setComment] = useState<Nullable<string>>(null);
  const { addExamComment } = useExamComments({ context: APOLLO_QUERY_CONTEXT });

  return (
    <Dialog
      open={isOpen}
      data-testid="critical-results-modal"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      slotProps={{
        paper: { sx: { width: 500, padding: '0 0 25px 0' } },
      }}
    >
      <DialogTitle sx={{ padding: '24px 24px 0 24px' }}>
        <Stack css="display: flex; justify-content: space-between;">
          <Stack space="xsmall" alignY="center">
            {titleIcon}
            <Text variant="display2">{title}</Text>
          </Stack>
          <IconButton
            onClick={() => {
              setIsOpen(false);
            }}
            sx={{ transform: 'translateX(12px)' }}
          >
            <CloseOutlinedIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack vertical space="medium">
          <Stack vertical space="xxsmall">
            {body}
          </Stack>
          <ExamCommentsModalSection smid={worklistItemSmid} onChangeComment={setComment} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ padding: '12px 24px 0px 24px' }}>
        <Button
          variant="ghost"
          useDefaultPadding={false}
          css={`
            padding: ${Spacings.xsmall}rem ${Spacings.medium}rem;
          `}
          onClick={(e) => {
            setIsOpen(false);
          }}
          data-testid="critical-results-modal-cancel"
        >
          Cancel
        </Button>
        <Button
          useDefaultPadding={false}
          css={`
            padding: ${Spacings.xsmall}rem ${Spacings.medium}rem;
          `}
          onClick={async (e) => {
            if (!isEmptyStringOrNull(comment)) {
              logger.info('[CriticalResultsModal] Adding exam comment from critical result modal', {
                comment,
                worklistItemSmid,
              });
              await addExamComment({ worklistItemSmid, comment: comment as string });
            }

            await onSubmit();
            setIsOpen(false);
          }}
          data-testid="critical-results-modal-confirm"
        >
          {actionLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CriticalResultsModal.displayName = 'CriticalResultsModal';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(CriticalResultsModal);
